/**
 * Centralized Feature Mapping Configuration
 * 
 * Maps UI elements to their required plan features.
 * If ANY feature from the required list is present in the plan, the UI element is shown.
 */

import {
  AGENT_FEATURES,
  BOB_V1_FEATURES,
  CONTRACT_ANALYSIS_FEATURES,
  PROPOSAL_ANALYSIS_FEATURES,
  SPECIFICATION_ANALYSIS_FEATURES,
  OPPORTUNITY_ANALYSIS_FEATURES,
  INTEGRATIONS_FEATURES,
  INN_DOC_FEATURES,
  GEOTECHNICAL_ANALYSIS_FEATURES,
  IPlan
} from '../../pricing/models';

/**
 * UI Feature Keys - Represents each UI element that can be shown/hidden
 */
export enum UI_FEATURE {
  // Sidebar Items
  ASK_BOB = 'ASK_BOB',
  GENERAL_AGENT = 'GENERAL_AGENT',
  PROJECTS = 'PROJECTS',
  DOCUMENTS = 'DOCUMENTS',
  ARCHIVED = 'ARCHIVED',
  EXPLORE = 'EXPLORE',

  // Settings Items - Integrations (basic = Signatures, SSO, Document processing; premium = all + connectors)
  INTEGRATIONS_BASIC = 'INTEGRATIONS_BASIC',
  INTEGRATIONS_PREMIUM = 'INTEGRATIONS_PREMIUM',

  // Document Analysis Features
  CONTRACT_ANALYSIS = 'CONTRACT_ANALYSIS',
  PROPOSAL_ANALYSIS = 'PROPOSAL_ANALYSIS',
  SPECIFICATION_ANALYSIS = 'SPECIFICATION_ANALYSIS',
  OPPORTUNITY_ANALYSIS = 'OPPORTUNITY_ANALYSIS',
  GEOTECHNICAL_ANALYSIS = 'GEOTECHNICAL_ANALYSIS',
}

/**
 * Feature Mapping: UI Element → Required Plan Features
 * 
 * If ANY feature in the array is present in the user's plan, the UI element is available.
 */
export const FEATURE_REQUIREMENTS: Record<UI_FEATURE, string[]> = {
  // Sidebar - Agent Features
  [UI_FEATURE.ASK_BOB]: Object.values(BOB_V1_FEATURES), // Bob v1 uses BOB_V1 features
  [UI_FEATURE.GENERAL_AGENT]: Object.values(AGENT_FEATURES), // Bob v2.0 uses AGENT features

  // Sidebar - InnDoc Features (require actual nested feature values, not just keys)
  // Projects/Documents/Archived are shown if ANY of the INN_DOC sub-features have values
  [UI_FEATURE.PROJECTS]: [
    ...Object.values(CONTRACT_ANALYSIS_FEATURES),
    ...Object.values(PROPOSAL_ANALYSIS_FEATURES),
    ...Object.values(SPECIFICATION_ANALYSIS_FEATURES),
    ...Object.values(OPPORTUNITY_ANALYSIS_FEATURES)
  ],
  [UI_FEATURE.DOCUMENTS]: [
    ...Object.values(CONTRACT_ANALYSIS_FEATURES),
    ...Object.values(PROPOSAL_ANALYSIS_FEATURES),
    ...Object.values(SPECIFICATION_ANALYSIS_FEATURES),
    ...Object.values(OPPORTUNITY_ANALYSIS_FEATURES)
  ],
  [UI_FEATURE.ARCHIVED]: [
    ...Object.values(CONTRACT_ANALYSIS_FEATURES),
    ...Object.values(PROPOSAL_ANALYSIS_FEATURES),
    ...Object.values(SPECIFICATION_ANALYSIS_FEATURES),
    ...Object.values(OPPORTUNITY_ANALYSIS_FEATURES)
  ],

  // Explore - Always visible (empty array means no requirements)
  [UI_FEATURE.EXPLORE]: [],

  // Settings - Integrations tab shown if basic OR premium
  [UI_FEATURE.INTEGRATIONS_BASIC]: [INTEGRATIONS_FEATURES.BASIC],
  [UI_FEATURE.INTEGRATIONS_PREMIUM]: [INTEGRATIONS_FEATURES.PREMIUM],

  // Document Analysis Features - Specific feature sets
  [UI_FEATURE.CONTRACT_ANALYSIS]: Object.values(CONTRACT_ANALYSIS_FEATURES),
  [UI_FEATURE.PROPOSAL_ANALYSIS]: Object.values(PROPOSAL_ANALYSIS_FEATURES),
  [UI_FEATURE.SPECIFICATION_ANALYSIS]: Object.values(SPECIFICATION_ANALYSIS_FEATURES),
  [UI_FEATURE.OPPORTUNITY_ANALYSIS]: Object.values(OPPORTUNITY_ANALYSIS_FEATURES),
  [UI_FEATURE.GEOTECHNICAL_ANALYSIS]: Object.values(GEOTECHNICAL_ANALYSIS_FEATURES),
};

/**
 * Response from feature availability check
 */
export interface FeatureAvailability {
  /**
   * Whether the feature is allowed based on plan features
   */
  allowed: boolean;

  /**
   * Payment/subscription status information
   * (For future use when payment checks are re-enabled)
   */
  status: {
    /**
     * Whether payment has been completed (subscription is ACTIVE)
     */
    isPaid: boolean;

    /**
     * Whether user is currently in trial period with days remaining
     */
    isTrialActive: boolean;

    /**
     * Whether subscription has ended
     */
    isExpired: boolean;

    /**
     * Number of trial days remaining (0 if not in trial or trial ended)
     */
    trialDaysLeft: number;

    /**
     * Raw subscription status
     */
    subscriptionStatus: string | null;
  };

  /**
   * List of matching features found in the plan
   * (Useful for debugging)
   */
  matchingFeatures: string[];
}

/**
 * Human-readable labels for each UI feature (used in downgrade warnings)
 */
export const UI_FEATURE_LABELS: Record<UI_FEATURE, string> = {
  [UI_FEATURE.ASK_BOB]: 'Ask Bob (AI Assistant)',
  [UI_FEATURE.GENERAL_AGENT]: 'Bob Agent',
  [UI_FEATURE.PROJECTS]: 'Projects',
  [UI_FEATURE.DOCUMENTS]: 'Documents',
  [UI_FEATURE.ARCHIVED]: 'Archived Documents',
  [UI_FEATURE.EXPLORE]: 'Explore',
  [UI_FEATURE.INTEGRATIONS_BASIC]: 'Basic Integrations',
  [UI_FEATURE.INTEGRATIONS_PREMIUM]: 'Premium Integrations & Connectors',
  [UI_FEATURE.CONTRACT_ANALYSIS]: 'Contract Analysis',
  [UI_FEATURE.PROPOSAL_ANALYSIS]: 'Proposal Analysis',
  [UI_FEATURE.SPECIFICATION_ANALYSIS]: 'Specification Analysis',
  [UI_FEATURE.OPPORTUNITY_ANALYSIS]: 'Opportunity Analysis',
  [UI_FEATURE.GEOTECHNICAL_ANALYSIS]: 'Geotechnical Analysis',
};

/**
 * Extract all flat feature strings from a plan object.
 * Pure function — no dependency on subscription state.
 */
export function getPlanFlatFeatures(plan: IPlan | null | undefined): string[] {
  if (!plan?.features) return [];
  const features: string[] = [];

  if (plan.features.BOB_V1) features.push(...plan.features.BOB_V1);
  if (plan.features.AGENT) features.push(...plan.features.AGENT);

  if (plan.features.INN_DOC) {
    if (plan.features.INN_DOC.CONTRACT_ANALYSIS?.length) {
      features.push(INN_DOC_FEATURES.CONTRACT_ANALYSIS);
      features.push(...plan.features.INN_DOC.CONTRACT_ANALYSIS);
    }
    if (plan.features.INN_DOC.PROPOSAL_ANALYSIS?.length) {
      features.push(INN_DOC_FEATURES.PROPOSAL_ANALYSIS);
      features.push(...plan.features.INN_DOC.PROPOSAL_ANALYSIS);
    }
    if (plan.features.INN_DOC.SPECIFICATION_ANALYSIS?.length) {
      features.push(INN_DOC_FEATURES.SPECIFICATION_ANALYSIS);
      features.push(...plan.features.INN_DOC.SPECIFICATION_ANALYSIS);
    }
    if (plan.features.INN_DOC.OPPORTUNITY_ANALYSIS?.length) {
      features.push(INN_DOC_FEATURES.OPPORTUNITY_ANALYSIS);
      features.push(...plan.features.INN_DOC.OPPORTUNITY_ANALYSIS);
    }
    if (plan.features.INN_DOC.GEOTECHNICAL_ANALYSIS?.length) {
      features.push(INN_DOC_FEATURES.GEOTECHNICAL_ANALSYIS);
    }
  }

  if (plan.features.INTEGRATIONS) features.push(...plan.features.INTEGRATIONS);

  return features;
}

/**
 * Check if a single UI_FEATURE is available in a given plan.
 * Pure function — mirrors FeatureFlagService.checkFeature but without subscription state.
 */
export function isPlanFeatureAvailable(plan: IPlan | null | undefined, uiFeature: UI_FEATURE): boolean {
  const requiredFeatures = FEATURE_REQUIREMENTS[uiFeature];
  if (!requiredFeatures || requiredFeatures.length === 0) return true;
  if (!plan?.features) return false;

  if (uiFeature === UI_FEATURE.ASK_BOB) {
    return !!plan.features.BOB_V1?.some(f => requiredFeatures.includes(f));
  }
  if (uiFeature === UI_FEATURE.GENERAL_AGENT) {
    return !!plan.features.AGENT?.some(f => requiredFeatures.includes(f));
  }

  const planFeatures = getPlanFlatFeatures(plan);
  return requiredFeatures.some(req => planFeatures.includes(req));
}

/**
 * Returns UI features present in currentPlan that are absent in newPlan.
 * Use to warn users about features they will lose on downgrade.
 */
export function getLostUIFeatures(currentPlan: IPlan | null | undefined, newPlan: IPlan | null | undefined): UI_FEATURE[] {
  return Object.values(UI_FEATURE).filter(feature =>
    isPlanFeatureAvailable(currentPlan, feature) &&
    !isPlanFeatureAvailable(newPlan, feature)
  );
}

/**
 * Grouping of related UI features for bulk checking
 */
export const FEATURE_GROUPS = {
  SIDEBAR_AGENT: [UI_FEATURE.ASK_BOB, UI_FEATURE.GENERAL_AGENT],
  SIDEBAR_INNDOC: [UI_FEATURE.PROJECTS, UI_FEATURE.DOCUMENTS, UI_FEATURE.ARCHIVED],
  ANALYSIS_MODULES: [
    UI_FEATURE.CONTRACT_ANALYSIS,
    UI_FEATURE.PROPOSAL_ANALYSIS,
    UI_FEATURE.SPECIFICATION_ANALYSIS,
    UI_FEATURE.OPPORTUNITY_ANALYSIS
  ]
} as const;
